import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Check, X, Users, Bell } from '@phosphor-icons/react'
import LoadingSpinner from './LoadingSpinner'

export default function PendingInvitations() {
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(null)

  useEffect(() => {
    loadInvitations()
  }, [])

  const loadInvitations = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: invites } = await supabase
        .from('invitations')
        .select(`
          *,
          inviter:inviter_id (
            name,
            email
          ),
          subgroup:subgroup_id (
            id,
            name
          )
        `)
        .eq('invitee_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })

      setInvitations(invites || [])
    } catch (error) {
      console.error('Error loading invitations:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAccept = async (invitation) => {
    setProcessing(invitation.id)

    try {
      const { data: { user } } = await supabase.auth.getUser()

      // Add user to subgroup
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: invitation.subgroup_id,
          user_id: user.id
        })

      if (memberError) throw memberError

      // Generate QR code and update role
      const qrSignature = `${user.id}-${Date.now()}`
      const { error: userError } = await supabase
        .from('users')
        .update({
          qr_signature: qrSignature,
          qr_generated_at: new Date().toISOString(),
          role: 'member' // Update from unassigned to member
        })
        .eq('id', user.id)

      if (userError) throw userError

      // Update invitation status
      const { error: updateError } = await supabase
        .from('invitations')
        .update({ 
          status: 'accepted',
          updated_at: new Date().toISOString()
        })
        .eq('id', invitation.id)

      if (updateError) throw updateError

      // Remove from list
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id))

      // Show success message
      alert(`Successfully joined ${invitation.subgroup.name}!`)

      // Refresh the page to update user's view
      window.location.reload()

    } catch (error) {
      console.error('Error accepting invitation:', error)
      alert('Failed to accept invitation: ' + error.message)
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async (invitation) => {
    if (!confirm('Are you sure you want to reject this invitation?')) return

    setProcessing(invitation.id)

    try {
      // Update invitation status
      const { error } = await supabase
        .from('invitations')
        .update({ 
          status: 'rejected',
        updated_at: new Date().toISOString()
        })
        .eq('id', invitation.id)

      if (error) throw error

      // Remove from list
      setInvitations(prev => prev.filter(inv => inv.id !== invitation.id))

    } catch (error) {
      console.error('Error rejecting invitation:', error)
      alert('Failed to reject invitation: ' + error.message)
    } finally {
      setProcessing(null)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading invitations..." />
  }

  if (invitations.length === 0) {
    return null
  }

  return (
    <div className="space-y-3 mb-4">
      <div className="flex items-center gap-2 mb-2">
        <Bell size={20} weight="fill" className="text-blue-600" />
        <h3 className="font-bold text-gray-900">Pending Invitations</h3>
      </div>

      {invitations.map((invitation) => (
        <div
          key={invitation.id}
          className="bg-blue-50 border border-blue-200 rounded-2xl p-4"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="bg-blue-100 p-2 rounded-xl flex-shrink-0">
              <Users size={24} weight="light" className="text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 mb-1">
                Join {invitation.subgroup?.name}
              </p>
              <p className="text-xs text-gray-600">
                <strong>{invitation.inviter?.name}</strong> invited you to join this subgroup
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(invitation.created_at).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleAccept(invitation)}
              disabled={processing === invitation.id}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Check size={18} weight="bold" />
              <span>{processing === invitation.id ? 'Accepting...' : 'Accept'}</span>
            </button>
            <button
              onClick={() => handleReject(invitation)}
              disabled={processing === invitation.id}
              className="flex-1 bg-white text-gray-700 px-4 py-2 rounded-xl font-medium hover:bg-gray-100 transition-colors border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <X size={18} weight="bold" />
              <span>{processing === invitation.id ? 'Rejecting...' : 'Reject'}</span>
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}